import {
  Card,
  CardBody,
  Flex,
  HStack,
  Stat,
  StatGroup,
  StatLabel,
  StatNumber,
} from "@chakra-ui/react";
import { FundFamilySummaryChartCapitalHistory } from "features/fund-family/summary/FundFamilySummaryChartCapitalHistory";
import { FundFamilySummaryChartCapitalStatus } from "features/fund-family/summary/FundFamilySummaryChartCapitalStatus";
import { FundFamilySummaryChartCreditFacility } from "features/fund-family/summary/FundFamilySummaryChartCreditFacility";
import { FundFamilySummaryChartFees } from "features/fund-family/summary/FundFamilySummaryChartFees";
import { FundFamilySummaryChartIRR } from "features/fund-family/summary/FundFamilySummaryChartIRR";
import { FundFamilySummaryChartNAV } from "features/fund-family/summary/FundFamilySummaryChartNAV";
import { useCreditFacilityListSuspense } from "gen/endpoints/credit-facility/credit-facility";
import { useInvestmentEntityStatsRetrieveSuspense } from "gen/endpoints/investment/investment";
import { useFundFamilyInvestorStatsRetrieveSuspense } from "gen/endpoints/investor-stats/investor-stats";
import { CreditFacilityData } from "gen/model/creditFacilityData";
import { useTranslation } from "react-i18next";
import PageBody from "shared/components/layout/PageBody";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { nowAsIsoDate } from "shared/utils/DateUtils";

export const FundFamilySummaryPage: React.FC = () => {
  const { t } = useTranslation(["fundFamily"]);
  const { fundFamilyId } = useRequiredStringParams();

  const { data: investmentStats } =
    useInvestmentEntityStatsRetrieveSuspense(fundFamilyId);

  const { data: investorStats } =
    useFundFamilyInvestorStatsRetrieveSuspense(fundFamilyId);

  const { data: creditFacilityList } =
    useCreditFacilityListSuspense(fundFamilyId);

  const hasActiveCreditFacility =
    creditFacilityList.length > 0 &&
    creditFacilityList.some(
      (cf: CreditFacilityData) => cf.maturity_date >= nowAsIsoDate(),
    );

  return (
    <PageContent>
      <PageHeader title={t("fundFamily:summary.title")} />
      <PageBody>
        <FundFamilySummaryChartIRR mb="24px" fundFamilyId={fundFamilyId} />
        <FundFamilySummaryChartNAV mb="24px" fundFamilyId={fundFamilyId} />
        <HStack spacing="16px" alignItems="stretch" mb="24px">
          {hasActiveCreditFacility && (
            <FundFamilySummaryChartCreditFacility
              fundFamilyId={fundFamilyId}
              creditFacilityId={creditFacilityList[0].id}
            />
          )}
          <Flex
            flexDirection={hasActiveCreditFacility ? "column" : "row"}
            gap="16px"
            width={hasActiveCreditFacility ? "auto" : "100%"}
            flexBasis={hasActiveCreditFacility ? "290px" : undefined}
          >
            <Card variant="chart" flex="1">
              <CardBody px="24px">
                <StatGroup alignItems="center">
                  <Stat size="md">
                    <StatNumber>
                      {investorStats.investor_entities_count}
                    </StatNumber>
                    <StatLabel>
                      {t("fundFamily:summary.charts.stats.investorEntities")}
                    </StatLabel>
                  </Stat>
                </StatGroup>
              </CardBody>
            </Card>
            <Card variant="chart" flex="1">
              <CardBody px="24px">
                <StatGroup alignItems="center">
                  <Stat size="md">
                    <StatNumber>
                      {investmentStats.active_investments}
                    </StatNumber>
                    <StatLabel>
                      {t("fundFamily:summary.charts.stats.totalInvestments")}
                    </StatLabel>
                  </Stat>
                </StatGroup>
              </CardBody>
            </Card>
          </Flex>
        </HStack>
        <HStack spacing="16px" alignItems="stretch" mb="24px">
          <FundFamilySummaryChartCapitalStatus
            flex={1}
            fundFamilyId={fundFamilyId}
          />
          <FundFamilySummaryChartCapitalHistory
            flex={1}
            fundFamilyId={fundFamilyId}
          />
        </HStack>
        <FundFamilySummaryChartFees mb="24px" fundFamilyId={fundFamilyId} />
      </PageBody>
    </PageContent>
  );
};
